import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, FileText, Briefcase, Calendar, BarChart3, Settings, LogOut, Menu, X, Building2, FileCheck, Link as LinkIcon, PhoneCall, Receipt, Car, MessageSquare, ShieldAlert } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";

const SETTINGS_PERMS = ["settings.manage", "settings.payments", "users.manage", "roles.manage", "api_keys.manage", "backup.manage", "ai.manage", "audit.view"];
// Navigation is filtered by permission keys (backend remains the source of truth)
export const NAV_ITEMS = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard, perm: "dashboard.view" },
  { name: "Customers", href: "/customers", icon: Users, perm: "customers.view" },
  { name: "Technicians", href: "/technicians", icon: Users, perm: "technicians.manage" },
  { name: "Quotes", href: "/quotes", icon: FileCheck, perm: "quotes.view" },
  { name: "Invoices", href: "/invoices", icon: FileText, perm: "invoices.view" },
  { name: "Messages", href: "/messages", icon: MessageSquare, perm: "messages.view" },
  { name: "Work Tracking", href: "/work", icon: Briefcase, perm: "work.view_own" },
  { name: "Appointments", href: "/appointments", icon: Calendar, perm: "appointments.view_own" },
  { name: "Reports", href: "/reports", icon: BarChart3, perm: "reports.view_operational" },
  { name: "Integrations", href: "/integrations", icon: LinkIcon, perm: "integrations.manage" },
  { name: "API Activity", href: "/activity", icon: PhoneCall, perm: "audit.view" },
  { name: "Expenses", href: "/expenses", icon: Receipt, perm: "expenses.view_own" },
  { name: "Mileage", href: "/mileage", icon: Car, perm: "mileage.view_own" },
  { name: "Settings", href: "/settings", icon: Settings, perm: SETTINGS_PERMS, always: true },
];

export default function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { loaded, canAny } = usePermissions();
  const allowed = (item) => !!item.always || (Array.isArray(item.perm) ? canAny(...item.perm) : canAny(item.perm));

  const [smsUnread, setSmsUnread] = useState(0);
  const canMessages = loaded && canAny("messages.view");
  useEffect(() => {
    if (!user || !canMessages) return;
    const load = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sms/unread-count`).then((r) => setSmsUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [user, canMessages]);
  const navigation = loaded ? NAV_ITEMS.filter(allowed) : [];
  const current = NAV_ITEMS.find((i) => i.href === location.pathname);
  const authorized = !loaded || !current || allowed(current);

  return (
    <div className="min-h-screen flex">
      {/* Sidebar for desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 text-white shadow-2xl">
        <div className="p-6 border-b border-blue-700/50">
          <div className="flex items-center space-x-3">
            <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
              <Building2 className="w-6 h-6" />
            </div>
            <h1 className="text-xl font-bold">Invoice Tracker</h1>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? "bg-white/20 text-white shadow-lg backdrop-blur-sm"
                    : "text-blue-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
                {item.name === "Messages" && smsUnread > 0 && <span className="ml-auto bg-white text-blue-800 text-xs font-bold rounded-full px-2 py-0.5" data-testid="nav-messages-unread">{smsUnread}</span>}
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-blue-700/50">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-3">
            <p className="text-sm text-blue-100 mb-1">Signed in as</p>
            <p className="font-semibold text-white" data-testid="user-name">{user?.full_name}</p>
            <p className="text-xs text-blue-200 truncate">@{user?.username}</p>
          </div>
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            variant="outline"
            className="w-full bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/40 backdrop-blur-sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 text-white shadow-2xl">
            <div className="p-6 border-b border-blue-700/50 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/10 p-2 rounded-lg">
                  <Building2 className="w-6 h-6" />
                </div>
                <h1 className="text-xl font-bold">Invoice Tracker</h1>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="flex-1 p-4 space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? "bg-white/20 text-white shadow-lg"
                        : "text-blue-100 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
            
            <div className="p-4 border-t border-blue-700/50">
              <div className="bg-white/10 rounded-xl p-4 mb-3">
                <p className="text-sm text-blue-100 mb-1">Signed in as</p>
                <p className="font-semibold text-white">{user?.full_name}</p>
                <p className="text-xs text-blue-200">@{user?.username}</p>
              </div>
              <Button
                onClick={onLogout}
                variant="outline"
                className="w-full bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0 max-w-full overflow-x-hidden">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between shadow-sm">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          <div className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <h1 className="font-bold text-slate-800">Invoice Tracker</h1>
          </div>
          <div className="w-6" />
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 min-w-0 max-w-full">
          {authorized ? children : (
            <div className="max-w-md mx-auto mt-16 text-center bg-white rounded-2xl border border-slate-200 p-8" data-testid="not-authorized">
              <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-slate-800">Not authorized</h2>
              <p className="text-sm text-slate-500 mt-1">Your role does not include access to this page. Ask an administrator if you need it.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
